import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import RowActions, { type RowAction } from '../../../../components/RowActions';
import type { SettingsMachineTemplateRow } from '../../types';
import { MACHINE_TEMPLATE_ROW_LABELS as LABELS } from '../../../../utils/labels';
import { ENGINE_TYPES } from '../../../planning/constants';

interface MachineTemplateRowProps {
  template: SettingsMachineTemplateRow;
  showDivider: boolean;
  onEdit?: () => void;
  onArchive?: () => void;
  onUnarchive?: () => void;
  onDelete?: () => void;
}

export default function MachineTemplateRow({
  template,
  showDivider,
  onEdit,
  onArchive,
  onUnarchive,
  onDelete,
}: MachineTemplateRowProps) {
  const actions: RowAction[] = template.archived
    ? onUnarchive
      ? [
          {
            label: LABELS.unarchive,
            icon: <UnarchiveOutlinedIcon fontSize="small" />,
            onClick: onUnarchive,
            color: 'success',
            testId: 'unarchive-button',
          },
        ]
      : []
    : [
        ...(onEdit
          ? [
              {
                label: LABELS.edit,
                icon: <EditIcon fontSize="small" />,
                onClick: onEdit,
                color: 'primary' as const,
                testId: 'edit-button',
              },
            ]
          : []),
        ...(onArchive
          ? [
              {
                label: LABELS.archive,
                icon: <ArchiveOutlinedIcon fontSize="small" />,
                onClick: onArchive,
                color: 'warning' as const,
                testId: 'archive-button',
              },
            ]
          : []),
        ...(onDelete
          ? [
              {
                label: LABELS.delete,
                icon: <DeleteOutlineIcon fontSize="small" />,
                onClick: onDelete,
                color: 'error' as const,
                testId: 'delete-button',
              },
            ]
          : []),
      ];

  const summaryParts = [
    template.category?.name,
    template.engine_type ? ENGINE_TYPES[template.engine_type].label : undefined,
    template.supplier?.name,
  ].filter((part): part is string => !!part);

  return (
    <div>
      {showDivider && <hr className="border-border-subtle" />}
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="flex-1 min-w-0">
          <p className={`font-medium mb-0 mt-0 truncate ${template.archived ? 'text-text-secondary' : ''}`}>
            {template.name}
          </p>
          {summaryParts.length > 0 && (
            <p className="text-xs mb-0 mt-0 text-text-secondary truncate">{summaryParts.join(' · ')}</p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <RowActions actions={actions} />
        </div>
      </div>
    </div>
  );
}
